import { useState, useEffect, type FormEvent } from 'react'
import { useNavigate } from 'react-router-dom'
import { EyeIcon, EyeOffIcon, Loader2 } from 'lucide-react'
import { toast } from 'sonner'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import Logo from '@/components/Logo'
import AuthBackgroundShape from '@/components/AuthBackgroundShape'
import { supabase } from '@/lib/supabase'
import { useAuth } from '@/hooks/useAuth'

const ResetPasswordPage = () => {
  const [isPasswordVisible, setIsPasswordVisible] = useState(false)
  const [isConfirmPasswordVisible, setIsConfirmPasswordVisible] = useState(false)
  const [password, setPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [isLoading, setIsLoading] = useState(false)
  const [hasValidSession, setHasValidSession] = useState<boolean | null>(null)
  const navigate = useNavigate()
  const { signOutSilently } = useAuth()

  useEffect(() => {
    const checkSession = async () => {
      const { data: { session } } = await supabase.auth.getSession()
      if (!session) {
        navigate('/auth/forgot-password')
        return
      }
      setHasValidSession(true)
    }
    checkSession()
  }, [navigate])

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault()

    if (password !== confirmPassword) {
      toast.error('Passwords do not match')
      return
    }

    if (password.length < 8) {
      toast.error('Password must be at least 8 characters')
      return
    }

    setIsLoading(true)

    try {
      const { data: { session }, error: sessionError } = await supabase.auth.getSession()
      
      if (sessionError || !session) {
        toast.error('Session expired. Please request a new password reset link.')
        setIsLoading(false)
        navigate('/auth/forgot-password')
        return
      }

      const { error } = await supabase.auth.updateUser({
        password: password,
      })

      if (error) {
        toast.error(error.message || 'Failed to reset password')
        setIsLoading(false)
        return
      }

      // Clear auth state FIRST (synchronously) to prevent flash of dashboard
      // Then navigate to login
      await signOutSilently()
      toast.success('Password reset successfully! Please sign in with your new password.')
      navigate('/auth/login', { replace: true })
    } catch (error) {
      console.error('Password reset error:', error)
      toast.error('Failed to reset password. Please try again.')
    } finally {
      setIsLoading(false)
    }
  }

  if (hasValidSession === null) {
    return (
      <div className='relative flex h-auto min-h-screen items-center justify-center overflow-x-hidden px-4 py-10 sm:px-6 lg:px-8'>
        <div className='absolute'>
          <AuthBackgroundShape />
        </div>
        <Card className='z-1 w-full border-none shadow-md sm:max-w-lg'>
          <CardContent className='flex items-center justify-center py-12'>
            <div className='flex flex-col items-center gap-3 text-center'>
              <Loader2 className='h-8 w-8 animate-spin text-primary' />
              <p className='text-sm text-muted-foreground'>Verifying session...</p>
            </div>
          </CardContent>
        </Card>
      </div>
    )
  }

  return (
    <div className='relative flex h-auto min-h-screen items-center justify-center overflow-x-hidden px-4 py-10 sm:px-6 lg:px-8'>
      <div className='absolute'>
        <AuthBackgroundShape />
      </div>

      <Card className='z-1 w-full border-none shadow-md sm:max-w-lg'>
        <CardHeader className='gap-3 items-center justify-center'>
          <Logo className='gap-3 mx-auto' showText={false} />
          <div>
            <CardTitle className='mb-1.5 text-2xl'>Set new password</CardTitle>
          </div>
        </CardHeader>

        <CardContent>
          <div className='space-y-4'>
            <form className='space-y-4' onSubmit={handleSubmit}>
              <div className='space-y-1'>
                <Label htmlFor='password' className='leading-5'>
                  New password*
                </Label>
                <div className='relative'>
                  <Input
                    id='password'
                    type={isPasswordVisible ? 'text' : 'password'}
                    placeholder='••••••••••••••••'
                    className='pr-9'
                    value={password}
                    onChange={e => setPassword(e.target.value)}
                    required
                    disabled={isLoading}
                  />
                  <Button
                    type='button'
                    variant='ghost'
                    size='icon'
                    onClick={() => setIsPasswordVisible(prevState => !prevState)}
                    className='text-muted-foreground focus-visible:ring-ring/50 absolute inset-y-0 right-0 rounded-l-none hover:bg-transparent'
                    disabled={isLoading}
                  >
                    {isPasswordVisible ? <EyeOffIcon /> : <EyeIcon />}
                    <span className='sr-only'>{isPasswordVisible ? 'Hide password' : 'Show password'}</span>
                  </Button>
                </div>
              </div>

              <div className='space-y-1'>
                <Label htmlFor='confirmPassword' className='leading-5'>
                  Confirm password*
                </Label>
                <div className='relative'>
                  <Input
                    id='confirmPassword'
                    type={isConfirmPasswordVisible ? 'text' : 'password'}
                    placeholder='••••••••••••••••'
                    className='pr-9'
                    value={confirmPassword}
                    onChange={e => setConfirmPassword(e.target.value)}
                    required
                    disabled={isLoading}
                  />
                  <Button
                    type='button'
                    variant='ghost'
                    size='icon'
                    onClick={() => setIsConfirmPasswordVisible(prevState => !prevState)}
                    className='text-muted-foreground focus-visible:ring-ring/50 absolute inset-y-0 right-0 rounded-l-none hover:bg-transparent'
                    disabled={isLoading}
                  >
                    {isConfirmPasswordVisible ? <EyeOffIcon /> : <EyeIcon />}
                    <span className='sr-only'>{isConfirmPasswordVisible ? 'Hide password' : 'Show password'}</span>
                  </Button>
                </div>
              </div>

              <Button className='w-full' type='submit' disabled={isLoading}>
                {isLoading ? 'Resetting password...' : 'Reset password'}
              </Button>
            </form>
          </div>
        </CardContent>
      </Card>
    </div>
  )
}

export default ResetPasswordPage

